import React from 'react';

const fmtPrice = (v) => parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const generatePackingSlipHTML = (order, storeInfo = {}) => {
  const sName = storeInfo.storeName || 'Storefront';
  // Empty defaults render as "—" in the slip header — better than
  // leaking a stale personal phone or email address into
  // a printed slip that goes out with the package.
  const sEmail = storeInfo.storeEmail || '—';
  const sPhone = storeInfo.storePhone || '—';
  const currency = storeInfo.currencySymbol || storeInfo.currency || 'USD';
  const mapHtml = order.deliveryAddress?.latitude && order.deliveryAddress?.longitude
    ? `<div style="margin-top:8px">
         <img src="https://staticmap.openstreetmap.de/staticmap.php?center=${order.deliveryAddress.latitude},${order.deliveryAddress.longitude}&zoom=15&size=300x150&markers=${order.deliveryAddress.latitude},${order.deliveryAddress.longitude},red-pushpin" 
              alt="Delivery location" style="width:100%;max-width:300px;border-radius:6px;border:1px solid #e5e7eb" />
       </div>`
    : '';

  const itemsRows = (order.items || []).map(item => `
    <tr>
      <td style="padding:8px 12px;border-bottom:1px solid #f0f0f0">${item.productName}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;color:#6b7280;font-size:13px">${item.productSku || '-'}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;text-align:center">${item.quantity}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;text-align:right">${fmtPrice(item.price)} ${currency}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:600">${fmtPrice(item.subtotal)} ${currency}</td>
    </tr>
  `).join('');

  const paymentLabel = order.paymentMethod === 'cod' ? 'Cash on Delivery'
    : order.paymentMethod === 'stripe' ? 'Credit Card (Stripe)'
    : order.paymentMethod === 'paypal' ? 'PayPal'
    : order.paymentMethod || 'N/A';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Order #${order.orderNumber} - Packing Slip</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1f2937; line-height: 1.5; padding: 32px; max-width: 800px; margin: 0 auto; }
    
    .slip-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 3px solid #4f46e5; }
    .slip-brand h1 { font-size: 26px; color: #4f46e5; margin-bottom: 4px; }
    .slip-brand p { font-size: 13px; color: #6b7280; }
    .slip-order-info { text-align: right; }
    .slip-order-info h2 { font-size: 18px; color: #4f46e5; margin-bottom: 4px; }
    .slip-order-info p { font-size: 13px; color: #6b7280; }
    .slip-status { display: inline-block; padding: 3px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; text-transform: capitalize; background: #eef2ff; color: #4f46e5; margin-top: 6px; }
    
    .slip-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 28px; }
    .slip-section h3 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; color: #4f46e5; margin-bottom: 8px; font-weight: 700; }
    .slip-section p { font-size: 14px; color: #374151; }
    
    .slip-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
    .slip-table thead th { padding: 10px 12px; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #4f46e5; border-bottom: 2px solid #4f46e5; font-weight: 700; }
    .slip-table thead th:nth-child(3) { text-align: center; }
    .slip-table thead th:nth-child(4),
    .slip-table thead th:nth-child(5) { text-align: right; }
    
    .slip-totals { width: 280px; margin-left: auto; }
    .slip-totals .row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 14px; color: #6b7280; }
    .slip-totals .row.final { border-top: 2px solid #4f46e5; padding-top: 10px; margin-top: 6px; font-weight: 700; font-size: 16px; color: #4f46e5; }
    
    .slip-footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 12px; color: #9ca3af; }
    .slip-footer strong { color: #4f46e5; }
    
    .slip-print-btn { display: block; margin: 0 auto 24px; padding: 10px 32px; background: #4f46e5; color: white; border: none; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; }
    .slip-print-btn:hover { background: #4338ca; }
    
    @media print {
      .slip-print-btn { display: none !important; }
      body { padding: 16px; }
      .slip-header { border-bottom-color: #4f46e5 !important; }
    }
  </style>
</head>
<body>
  <button class="slip-print-btn" onclick="window.print()">Print Packing Slip</button>

  <div class="slip-header">
    <div class="slip-brand">
      <h1>${sName}</h1>
      <p>${sEmail}</p>
    </div>
    <div class="slip-order-info">
      <h2>Order #${order.orderNumber}</h2>
      <p>${new Date(order.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      <span class="slip-status">${order.status}</span>
    </div>
  </div>

  <div class="slip-grid">
    <div class="slip-section">
      <h3>Customer</h3>
      <p><strong>${order.client?.name || ''}</strong></p>
      <p>${order.client?.email || ''}</p>
      <p>${order.client?.phone || ''}</p>
    </div>
    <div class="slip-section">
      <h3>Delivery Address</h3>
      <p>${order.deliveryAddress?.fullAddress || order.deliveryAddress?.street || ''}</p>
      ${mapHtml}
    </div>
  </div>

  ${order.paymentMethod ? `
  <div class="slip-section" style="margin-bottom:20px">
    <h3>Payment</h3>
    <p>${paymentLabel}</p>
  </div>` : ''}

  <table class="slip-table">
    <thead>
      <tr>
        <th>Product</th>
        <th>SKU</th>
        <th>Qty</th>
        <th>Price</th>
        <th>Subtotal</th>
      </tr>
    </thead>
    <tbody>
      ${itemsRows}
    </tbody>
  </table>

  <div class="slip-totals">
    <div class="row"><span>Subtotal</span><span>${fmtPrice(order.subtotal)} ${currency}</span></div>
    <div class="row"><span>Shipping</span><span>${parseFloat(order.shippingCost) === 0 ? 'Free' : `${fmtPrice(order.shippingCost)} ${currency}`}</span></div>
    <div class="row"><span>Tax</span><span>${fmtPrice(order.tax)} ${currency}</span></div>
    <div class="row final"><span>Total</span><span>${fmtPrice(order.total)} ${currency}</span></div>
  </div>

  ${order.notes ? `
  <div class="slip-section" style="margin-top:24px;padding:16px;background:#f9fafb;border-radius:8px;border-left:4px solid #4f46e5">
    <h3>Order Notes</h3>
    <p>${order.notes}</p>
  </div>` : ''}

  <div class="slip-footer">
    <p>Thank you for shopping with <strong>${sName}</strong>!</p>
    <p style="margin-top:4px">${sName} | ${sEmail} | ${sPhone}</p>
  </div>
</body>
</html>`;
};

export const openPackingSlip = (order, storeInfo) => {
  const html = generatePackingSlipHTML(order, storeInfo);
  const printWindow = window.open('', '_blank', 'width=850,height=1100');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
};

const PackingSlipButton = ({ order, label, className, storeInfo }) => {
  return (
    <button
      type="button"
      className={className || 'packing-slip-btn'}
      onClick={() => openPackingSlip(order, storeInfo)}
    >
      {label || 'Print Order'}
    </button>
  );
};

export default PackingSlipButton;
